import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Alert, Message } from 'rsuite';
import { Redirect, withRouter } from 'react-router';

// redux
import {
  fetchCampaignInfo,
  fetchCampaignElementTypes,
  getCampaignElementIds,
  getHasLoadingError,
  getHasProcessingSubmissionError,
  getHasSubmittedForProcessing,
  getIsCampaignLandingLoading,
  getLoadingErrorMessage,
  getUserIpAddress,
  loadPreviewData,
  setPreviewError,
  putCampaignCount,
  getCampaignId,
  getCustomCampaignSuccessPageData,
  getIsSubmittingForProcessing,
  getProcessingSubmissionErrorMessage,
  getCampaignCustomDomain,
  getIsCampaignLandingLoaded,
  getUseCampaignProfileSection,
  getUseCampaignLogo,
  getIsCampaignDisabled
} from '../../redux/modules/campaignLandingPage';
// components
import ElementFacade from '../../components/CampaignLanding/ElementFacade';
import SubmitResponseButton from '../../components/CampaignLanding/SubmitResponseButton';
import CurrentLinkModal from '../../components/CampaignLanding/CurrentLinkModal';
// utils
import { getQueryParams } from '../../helpers/commonHelper';
import { getCampaignPreview } from '../../utils/windowStorage';
// styles
import styles from './LandingPage.module.less';
import { preventCustomDomainClpAccess } from '../../helpers/customDomainsHelper';
import { useVpSnackbar, VpHookFormWrapper } from '../../vp-ui';

import VpSplash from './VpSplash';
import LandingPagePreviewBanner from './LandingPagePreviewBanner';
import APP_ROUTES from '@src/app_routes';

const LandingPage = withRouter(
  ({
    _fetchCampaignInfo,
    _fetchCampaignElementTypes,
    _getUserIpAddress,
    _loadPreviewData,
    _putCampaignCount,
    campaignId,
    campaignElementIds,
    hasLoadingError,
    hasProcessingSubmissionError,
    hasSubmittedForProcessing,
    isCampaignLandingLoading,
    loadingErrorMessage,
    customLandingPage,
    isSubmittingForProcessing,
    processingSubmissionErrorMessage,
    isCampaignLandingLoaded,
    campaignCustomDomain,
    isUsingProfileSection,
    isUsingCampaignLogo,
    campaignDisabled,
    history
  }) => {
    // Pull the campaignLink from the URL
    const locationArray = window.location.pathname.split('/');
    const campaignLink = locationArray[locationArray.length - 1];
    const queryParams = getQueryParams(window.location.search);

    const { addSnackbarSuccess } = useVpSnackbar();

    useEffect(() => {
      if (hasSubmittedForProcessing) {
        !queryParams.preview && addSnackbarSuccess({ message: `Your submission was successful!` });

        if (
          customLandingPage &&
          customLandingPage.type === 'REDIRECT' &&
          customLandingPage.redirectUrl
        ) {
          const timer = setTimeout(() => {
            window.location = `${customLandingPage.redirectUrl}`;
          }, 2000);
          return () => clearTimeout(timer);
        } else history.push(`/c/${campaignLink}/success`);
      }
    }, [hasSubmittedForProcessing]);

    // Initiate loading the data from the API
    useEffect(() => {
      _fetchCampaignElementTypes();
      if (queryParams.preview) {
        try {
          const campaignPreview = getCampaignPreview();
          const previewValues = JSON.parse(campaignPreview);

          // Dispatch deserialized value to store
          _loadPreviewData(previewValues);
        } catch (e) {
          window.location.href = `${window.location.origin}/c/${campaignLink}`;
        }
      } else {
        _fetchCampaignInfo(campaignLink);
        _getUserIpAddress();
      }
    }, []);

    useEffect(() => {
      if (campaignId && !queryParams.preview) {
        _putCampaignCount(campaignId);
      }
    }, [campaignId]);

    useEffect(() => {
      if (isCampaignLandingLoaded) {
        preventCustomDomainClpAccess(campaignCustomDomain);
      }
    }, [isCampaignLandingLoaded]);

    useEffect(() => {
      // If campaign fails to save, show a toast
      if (hasProcessingSubmissionError) {
        const submitErrorMessage = processingSubmissionErrorMessage
          ? ` ${processingSubmissionErrorMessage}.`
          : '';
        Alert.error(`Failed to submit campaign response.${submitErrorMessage}`, 7500);
      }
    }, [isSubmittingForProcessing, hasProcessingSubmissionError, processingSubmissionErrorMessage]);

    if (hasLoadingError) {
      return (
        <Message
          showIcon
          className={styles.vpCampaignLoadingError}
          type="warning"
          title="Warning"
          description={loadingErrorMessage}
        />
      );
    }

    if (isCampaignLandingLoading) {
      return <div>Loading...</div>;
    }

    let campaignBrandingSpacing = '';

    if (isUsingProfileSection && isUsingCampaignLogo) {
      campaignBrandingSpacing = styles.fullBranding;
    } else if (isUsingCampaignLogo) {
      campaignBrandingSpacing = styles.logoBranding;
    } else if (isUsingProfileSection) {
      campaignBrandingSpacing = styles.profileBranding;
    }

    const mainClasses = `
      ${styles.vpCampaignLandingPageContainer}
      ${campaignBrandingSpacing}
    `;

    if (campaignDisabled) {
      return <Redirect to={APP_ROUTES.notFound} />;
    }

    return (
      <>
        <VpSplash />
        <main className={mainClasses}>
          <section className={styles.vpCampaignLandingInnerWrapper}>
            <CurrentLinkModal />
            <section>
              <LandingPagePreviewBanner />
            </section>
            <section>
              <VpHookFormWrapper onSubmit={() => {}}>
                {campaignElementIds.map((id) => (
                  <ElementFacade key={id} qid={id} />
                ))}
              </VpHookFormWrapper>
            </section>
            <section className={styles.vpSubmitSection}>
              {campaignId && <SubmitResponseButton />}
            </section>
          </section>
        </main>
      </>
    );
  }
);

LandingPage.propTypes = {
  _fetchCampaignInfo: PropTypes.func,
  _fetchCampaignElementTypes: PropTypes.func,
  _getUserIpAddress: PropTypes.func,
  _loadPreviewData: PropTypes.func,
  _setPreviewError: PropTypes.func,
  _putCampaignCount: PropTypes.func,
  campaignElementIds: PropTypes.array,
  isCampaignLandingLoading: PropTypes.bool,
  hasLoadingError: PropTypes.bool,
  hasProcessingSubmissionError: PropTypes.bool,
  hasSubmittedForProcessing: PropTypes.bool,
  loadingErrorMessage: PropTypes.string,
  campaignId: PropTypes.number,
  customLandingPage: PropTypes.object,
  isSubmittingForProcessing: PropTypes.bool,
  processingSubmissionErrorMessage: PropTypes.string,
  campaignCustomDomain: PropTypes.string,
  isCampaignLandingLoaded: PropTypes.bool,
  isUsingProfileSection: PropTypes.bool,
  isUsingCampaignLogo: PropTypes.bool
};

function mapStateToProps(state) {
  return {
    campaignElementIds: getCampaignElementIds(state),
    isCampaignLandingLoading: getIsCampaignLandingLoading(state),
    hasLoadingError: getHasLoadingError(state),
    hasProcessingSubmissionError: getHasProcessingSubmissionError(state),
    loadingErrorMessage: getLoadingErrorMessage(state),
    hasSubmittedForProcessing: getHasSubmittedForProcessing(state),
    isSubmittingForProcessing: getIsSubmittingForProcessing(state),
    campaignId: getCampaignId(state),
    customLandingPage: getCustomCampaignSuccessPageData(state),
    processingSubmissionErrorMessage: getProcessingSubmissionErrorMessage(state),
    campaignCustomDomain: getCampaignCustomDomain(state),
    isCampaignLandingLoaded: getIsCampaignLandingLoaded(state),
    isUsingProfileSection: getUseCampaignProfileSection(state),
    isUsingCampaignLogo: getUseCampaignLogo(state),
    campaignDisabled: getIsCampaignDisabled(state)
  };
}

const mapDispatchToProps = {
  _fetchCampaignInfo: fetchCampaignInfo,
  _fetchCampaignElementTypes: fetchCampaignElementTypes,
  _getUserIpAddress: getUserIpAddress,
  _loadPreviewData: loadPreviewData,
  _putCampaignCount: putCampaignCount,
  _setPreviewError: setPreviewError
};

export default connect(mapStateToProps, mapDispatchToProps)(LandingPage);



// WEBPACK FOOTER //
// ./src/containers/CampaignLandingPage/LandingPage.jsx